/**
 * Fresh handshake fixture — the canonical happy-path scenario for offline
 * client development.
 *
 * Timeline:
 *   t=0 ms   HandshakeAck (Fresh mode, tick=0, tick_hz=2)
 *   t=5 ms   Snapshot (16×16 world, 1 player at centre, Clear weather, empty fog)
 *   t=500 ms Event(WorldEvents) — PlayerJoined + PlayerMoved to (9,8)
 *   t=1000 ms Event(WorldEvents) — PlayerMoved to (10,8)
 *   t=15 000 ms Ping (exercises pong round-trip)
 *
 * The scenario also wires an `onIntent` echo for DM-send so DM-pane UI work
 * can round-trip locally.
 */

import type {
  ClientIntent,
  DmDeliveryPayload,
  EventEnvelope,
  MockScenario,
  PlayerSnapshot,
  ServerHandshakeAck,
  ServerMessage,
  SnapshotMessage,
  WorldSnapshot,
} from '../../wire';

const WORLD_W = 16;
const WORLD_H = 16;

// P1.8 C5 shape: record-list WorldSnapshot. No dense tile/biome arrays
// on the wire; the applier zero-fills those internally so the renderer
// can still paint a uniform floor against the mock fixture.
const worldSnapshot: WorldSnapshot = {
  bounds: [WORLD_W, WORLD_H],
  clock: { tick: 0n, day_tick: 900, day: 0, season: 'Spring', season_day: 0, year: 0, phase: 'Day' },
  arrival_zone: { center: [8, 8], radius: 3 },
  walls: [],
  doors: [],
  props: [],
  rooms: [],
  tile_elevations: [],
};

const alice: PlayerSnapshot = {
  id: 1,
  display_name: 'Alice',
  discriminator: null,
  role: 'Player',
  position: [8, 8],
  facing: 'South',
  action_status: 'Idle',
  avatar: {
    palette: {
      hair: '#8b3a2a',
      shirt: '#d97239',
      pants: '#563223',
      accent: '#f2c57c',
    },
    sprite_sheet: 'DefaultHumanoid',
  },
  connection_status: 'Connected',
};

const handshakeAck: ServerHandshakeAck = {
  protocol_version: 1,
  server_build: 'mytile-server/0.0.0+mock',
  player_id: alice.id,
  display_name: alice.display_name,
  world_id: 1,
  tick: 0n,
  tick_hz: 2,
  server_time_ms: 1n,
  client_config: { rendering: {}, input: {}, ui: {}, chat: {} },
  reconnect_mode: { kind: 'Fresh' },
};

const snapshot: SnapshotMessage = {
  tick: 0n,
  world: worldSnapshot,
  players: [alice],
  self_inventory: { items: [], capacity: 16 },
  self_recent_utterances: [],
  weather: { kind: 'Clear', intensity: 0, target_at: 0n, temperature: 20, apparent_temperature: 18, humidity: 0.5, pressure: 0.5, wind_direction: 0, wind_speed: 0, cloud_cover: 0, precip_intensity: 0, precip_type: 'None', thunderstorm_active: false, fog_density: 0, visibility: 1 },
  fog: { bitmap: [] },
};

const playerJoinedBatch: EventEnvelope = {
  envelope_kind: 'WorldEvents',
  data: {
    tick: 1n,
    events: [
      {
        PlayerJoined: {
          player: alice.id,
          position: [8, 8],
          display_name: alice.display_name,
        },
      },
    ],
  },
};

const playerMovedBatch1: EventEnvelope = {
  envelope_kind: 'WorldEvents',
  data: {
    tick: 1n,
    events: [
      {
        PlayerMoved: {
          player: alice.id,
          from: [8, 8],
          to: [9, 8],
          facing: 'East',
        },
      },
    ],
  },
};

const playerMovedBatch2: EventEnvelope = {
  envelope_kind: 'WorldEvents',
  data: {
    tick: 2n,
    events: [
      {
        PlayerMoved: {
          player: alice.id,
          from: [9, 8],
          to: [10, 8],
          facing: 'East',
        },
      },
    ],
  },
};

export const freshHandshake: MockScenario = {
  name: 'fresh-handshake',
  description:
    '1-player Fresh-mode handshake + 16×16 Clear snapshot + two tick-stepped PlayerMoved events + a late Ping.',
  steps: [
    { delay_ms_from_connect: 0, message: { type: 'HandshakeAck', payload: handshakeAck } },
    { delay_ms_from_connect: 5, message: { type: 'Snapshot', payload: snapshot } },
    { delay_ms_from_connect: 500, message: { type: 'Event', payload: playerJoinedBatch } },
    { delay_ms_from_connect: 500, message: { type: 'Event', payload: playerMovedBatch1 } },
    { delay_ms_from_connect: 1000, message: { type: 'Event', payload: playerMovedBatch2 } },
    {
      delay_ms_from_connect: 15000,
      message: { type: 'Ping', payload: { nonce: 1n, server_tick: 30n } },
    },
  ],
  onIntent: (intent: ClientIntent): ServerMessage[] => {
    // DM-send round-trip: echo back a DmDelivery with the same content so
    // DM-pane UI can verify optimistic-send → canonical-replace locally.
    if (
      intent.plane === 'Gateway' &&
      intent.intent.kind === 'DmSend'
    ) {
      const delivery: DmDeliveryPayload = {
        // P1.8 C5 boundary: wire ConversationId is a number; the
        // hand-rolled DmDeliveryPayload still uses string (client-store
        // convention). Stringify here as a pragmatic bridge; the full
        // string↔number reconciliation is a separate carry-forward.
        conversation_id: String(intent.intent.data.conversation_id),
        message: {
          id: `mock-msg-${Date.now()}`,
          sender: alice.id,
          content: intent.intent.data.content,
          sent_at: BigInt(Date.now()),
          edited: false,
          deleted: false,
        },
      };
      return [
        {
          type: 'Event',
          payload: { envelope_kind: 'DmDelivery', data: delivery },
        },
      ];
    }
    return [];
  },
};
